import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SafeHtml from './SafeHtml.tsx';

interface ComponentProps {
  html?: string;
  className?: string;
  // Background of the Expand/Collapse pill, which overlays the note. Only needs overriding on a
  // surface that is not white (e.g. the InfoNote alert box).
  bgClassName?: string;
  dataTestId?: string;
}

// 3 lines of the 14px note text (line-height 18px). The typography is pinned below so the clamp
// lands on a whole line on every surface, not only inside a tag.
const COLLAPSED_MAX_HEIGHT = 54;

// Figma fades the last 32px of the clamped note out. A mask fades the text itself rather than
// painting a coloured gradient over it, so it works on any background.
const FADE_MASK = 'linear-gradient(to top, transparent 0px, black 32px)';

const CollapsibleNote: React.FC<ComponentProps> = ({ html, className, bgClassName = 'bg-white', dataTestId }) => {
  const { tr } = useTranslationLgs();
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // The clamp lives on the wrapper, so the inner block still lays out at its natural height and
  // reports the real line count. A width change reflows it, which the observer sees as a height change.
  const measure = useCallback(() => {
    const el = contentRef.current;
    if (el) setOverflowing(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
  }, []);

  useEffect(() => {
    measure();
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [html, measure]);

  const clamped = overflowing && !expanded;

  return (
    <div className={twMerge('relative max-w-[750px]', className)}>
      <div
        className={clsx('overflow-hidden', !expanded && 'max-h-[54px]')}
        style={clamped ? { maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK } : undefined}>
        <div ref={contentRef}>
          <SafeHtml html={html} dataTestId={dataTestId} className="text-sm font-medium leading-[18px]" />
        </div>
      </div>

      {overflowing && (
        <button
          type="button"
          data-testid={dataTestId ? `${dataTestId}-expand` : undefined}
          // Inside ButtonTag this sits within <summary>, whose click handler opens the edit popup.
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded(v => !v);
          }}
          // border-0/p-0/font-family reset: a bare <button> otherwise renders with the UA's outset border and Arial.
          className={twMerge('absolute bottom-0 right-0 flex cursor-pointer items-center gap-1.5 border-0 p-0 pl-2 text-sm font-medium leading-none text-content-brand [font-family:inherit] transition-all duration-200 hover:text-complementary-brandDark', bgClassName)}>
          {expanded ? tr('generic.collapse', 'Collapse') : tr('generic.expand', 'Expand')}
          <SvgChevron className={clsx('w-4 h-4 text-content-brand', expanded && 'rotate-180')} />
        </button>
      )}
    </div>
  );
};

export default CollapsibleNote;
